/**
 * Invite acceptance page — handles the magic link from team invite emails.
 *
 * Flow:
 *   1. Land on /invite/:token (no auth required to LOAD the page).
 *   2. Look up the invitation via the public token endpoint to display
 *      team name, role, and inviter so the user knows what they're accepting.
 *   3. If signed out → "Sign in to accept" CTA that sends the invitee's
 *      address along (`email=`) and returns to the same /invite/:token URL.
 *   4. If signed in → "Accept invitation" button that POSTs to the accept
 *      endpoint and hands off to the slim invited wizard at
 *      /onboarding?invited=1 on success (Fix 5).
 *
 * The token IS the authorisation. A signed-in account whose email differs
 * from the invited one can still accept — the recipient may have forwarded
 * the link or signed up with a different address — and is told about the
 * mismatch with "Switch account instead" as the quiet alternative.
 *
 * The ONE account that cannot accept is the inviter's own (the server answers
 * SELF_ACCEPT, routes/teams.ts). Measured 2026-10-09: an owner opened the
 * invitee's link while signed in as themselves, met "sign out and sign in
 * with the invited email" beside a live Accept, signed out, and never came
 * back in as the invitee. The inviter now gets no Accept and one primary
 * action, "Switch account to accept", which signs out straight to the sign-in
 * page with the invitee's address prefilled and the invitation as the
 * destination (`signedOut` clears residual Clerk sessions, useAuthCardSubmit).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { HugeiconsIcon } from '@hugeicons/react';
import { Alert01Icon, UserAdd01Icon } from '@hugeicons/core-free-icons';
import { useAuth, useClerk, useUser } from '../lib/auth';
import { teamsApi, type InvitationLookup } from '../lib/api/teams';
import { useAcceptTeamInvitation } from '../hooks/useTeams';
import { INVITED_STORAGE_KEY } from '../components/onboarding/OnboardingWizard';
import { AuthShell } from '../components/auth/AuthShell';
import { Button, buttonVariants } from '../components/ui/button';
import { INVITE_ROLE_OPTIONS } from '../lib/inviteEmail';
import { log } from '@/lib/logger';
import { capture } from '@/lib/analytics';

log.info('[InvitePage] Module loaded');

type LookupState =
  | { status: 'loading' }
  | { status: 'ready'; invitation: InvitationLookup }
  | { status: 'error'; message: string };

/** Who is looking at the invitation, relative to the address it was sent to. */
type Viewer = 'invitee' | 'inviter' | 'other';

/**
 * The sign-in page with the invitee's address prefilled (SignInPage reads
 * `email`) and the invitation as the post-auth destination. `signedOut` is
 * the post-sign-out marker useAuthCardSubmit reads to clear residual sessions.
 */
function signInUrl(token: string, inviteeEmail: string, signedOut = false): string {
  const params = new URLSearchParams();
  if (signedOut) params.set('signedOut', '1');
  params.set('email', inviteeEmail);
  params.set('redirect_url', `/invite/${token}`);
  return `/sign-in?${params.toString()}`;
}

function resolveViewer(signedInEmail: string | null, invitation: InvitationLookup): Viewer {
  if (!signedInEmail) return 'invitee';
  const me = signedInEmail.toLowerCase();
  if (me === invitation.email.toLowerCase()) return 'invitee';
  if (invitation.invitedBy?.email && me === invitation.invitedBy.email.toLowerCase()) return 'inviter';
  return 'other';
}

/** The DS warning pattern (`.alert-warning`, styles/index.css) with its icon. */
function MismatchNotice({ children }: { children: ReactNode }) {
  return (
    <div role="status" className="alert-warning flex items-start gap-2 text-left">
      <HugeiconsIcon icon={Alert01Icon} size={18} strokeWidth={2} color="currentColor" className="mt-1 shrink-0 text-yellow-600" aria-hidden="true" />
      <p className="min-w-0 break-words">{children}</p>
    </div>
  );
}

const QUIET_ACTION_CLASS = 'text-sm font-medium text-gray-500 hover:text-gray-700 hover:underline';

export function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const acceptMutation = useAcceptTeamInvitation();

  // Accept BEFORE bootstrap (Fix 5). The accept endpoint auto-creates the
  // users row itself (routes/teams.ts), so bootstrapping first is not needed
  // and would create a personal team + default project for a brand-new
  // invitee. The session query is invalidated after a successful accept
  // instead, so the next bootstrap sees the invited membership and skips
  // the personal-team path.

  const [lookup, setLookup] = useState<LookupState>({ status: 'loading' });
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLookup({ status: 'error', message: 'Missing invitation token.' });
      return;
    }
    if (!isLoaded) return;
    let cancelled = false;
    setLookup({ status: 'loading' });
    Promise.resolve(isSignedIn ? getToken() : undefined)
      .then(authToken => teamsApi.lookupInvitation(token, authToken ?? undefined))
      .then((invitation) => {
        if (cancelled) return;
        setLookup({ status: 'ready', invitation });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setLookup({
          status: 'error',
          message: err.message || 'This invitation is not valid.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [token, isLoaded, isSignedIn, user?.id, getToken]);

  if (!token) {
    return <Navigate to="/sign-in" replace />;
  }

  const handleAccept = () => {
    setAcceptError(null);
    acceptMutation.mutate(
      { inviteToken: token },
      {
        onSuccess: (result) => {
          if (lookup.status === 'ready') {
            capture('invitation_accepted', {
              role: lookup.invitation.role,
            });
          }
          // Signal the invited slim wizard (Fix 5). The stored teamId lets
          // the wizard greet the user with the joined team's name even after
          // a refresh drops the ?invited=1 param.
          try {
            sessionStorage.setItem(INVITED_STORAGE_KEY, result.teamId);
          } catch {
            // ignore, storage access can throw in private mode
          }
          // Invalidate the session AFTER the accept so bootstrap re-runs with
          // the invited membership in place (memberships > 0 skips creating a
          // personal team + default project).
          queryClient.invalidateQueries({ queryKey: ['app-session'] });
          navigate('/onboarding?invited=1', { replace: true });
        },
        onError: (err) => {
          setAcceptError(err.message || 'Failed to accept invitation.');
        },
      },
    );
  };

  // Sign out straight to the sign-in page with the invitee's address prefilled,
  // the same Clerk call the avatar menu makes (UserMenuButton.tsx).
  const handleSwitchAccount = (state: 'inviter' | 'other', inviteeEmail: string) => {
    log.info('[InvitePage] switch account', { state });
    capture('invite_switch_account_clicked', { state });
    void signOut({ redirectUrl: signInUrl(token, inviteeEmail, true) });
  };

  const signedInEmail = user?.primaryEmailAddress?.emailAddress ?? null;

  return (
    <AuthShell>
      <div className="auth-card space-y-6 text-center animate-fade-in">
        <div className="flex justify-center">
          <HugeiconsIcon icon={UserAdd01Icon} size={48} strokeWidth={1.5} color="currentColor" className="text-primary-500" aria-hidden="true" />
        </div>

        <div className="space-y-1">
          <h1 className="hero-title">Team invitation</h1>
          {lookup.status === 'ready' && (
            <p className="text-sm text-gray-500">
              {lookup.invitation.invitedBy?.name
                ? `${lookup.invitation.invitedBy.name} invited you to join`
                : 'You have been invited to join'}
            </p>
          )}
        </div>

        {lookup.status === 'loading' && (
          <p className="text-sm text-gray-500">Loading invitation...</p>
        )}

        {lookup.status === 'error' && (
          <div className="space-y-6">
            <p className="text-sm text-red-600" role="alert">
              {lookup.message}
            </p>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={() => navigate('/dashboard', { replace: true })}
            >
              Go to dashboard
            </Button>
          </div>
        )}

        {lookup.status === 'ready' && (() => {
          const { invitation } = lookup;
          const roleLabel = INVITE_ROLE_OPTIONS.find((o) => o.value === invitation.role)?.label ?? invitation.role;
          const viewer = resolveViewer(signedInEmail, invitation);
          return (
            <div className="space-y-6">
              <div className="rounded-lg border border-gray-200 bg-white p-4 text-left">
                <p className="oq-text-heading text-gray-900">{invitation.team.name}</p>
                <p className="mt-1 text-xs text-gray-500">
                  Role: <span className="font-medium text-gray-700">{roleLabel}</span>
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Sent to: <span className="font-medium text-gray-700">{invitation.email}</span>
                </p>
              </div>

              {!isLoaded ? (
                <p className="text-sm text-gray-500">Checking sign-in status...</p>
              ) : !isSignedIn ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Sign in or create an account to accept this invitation.
                  </p>
                  <a
                    href={signInUrl(token, invitation.email)}
                    className={buttonVariants({ variant: 'primary', size: 'md', className: 'w-full' })}
                  >
                    <span className="text-cap-trim">Sign in to accept</span>
                  </a>
                  <a
                    href={signInUrl(token, invitation.email)}
                    className={buttonVariants({ variant: 'secondary', size: 'md', className: 'w-full' })}
                  >
                    <span className="text-cap-trim">Create an account</span>
                  </a>
                </div>
              ) : viewer === 'inviter' && !invitation.accepted ? (
                <div className="space-y-3">
                  <MismatchNotice>
                    You sent this invitation to <span className="font-medium">{invitation.email}</span>. It can only be accepted from that&nbsp;account.
                  </MismatchNotice>
                  <Button type="button" className="w-full" onClick={() => handleSwitchAccount('inviter', invitation.email)}>
                    Switch account to accept
                  </Button>
                  <Link to="/dashboard" className={`inline-block ${QUIET_ACTION_CLASS}`}>
                    <span className="text-cap-trim block">Go to dashboard</span>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {viewer === 'other' && (
                    <MismatchNotice>
                      You are signed in as <span className="font-medium">{signedInEmail}</span>, but this invitation was sent to <span className="font-medium">{invitation.email}</span>.
                    </MismatchNotice>
                  )}
                  <Button
                    type="button"
                    className="w-full"
                    onClick={handleAccept}
                    disabled={acceptMutation.isPending}
                  >
                    {acceptMutation.isPending ? 'Joining...' : invitation.accepted ? 'Continue to team' : 'Accept invitation'}
                  </Button>
                  {viewer === 'other' && (
                    <button type="button" className={QUIET_ACTION_CLASS} onClick={() => handleSwitchAccount('other', invitation.email)}>
                      <span className="text-cap-trim block">Switch account instead</span>
                    </button>
                  )}
                  {acceptError && (
                    <p className="text-sm text-red-600" role="alert">
                      {acceptError}
                    </p>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-400">
                Expires{' '}
                {new Date(invitation.expiresAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>
          );
        })()}
      </div>
    </AuthShell>
  );
}
